import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewsSection = ({ limit = 6 }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data.slice(0, limit));
      } catch (error) {
        console.error('Error fetching reviews:', error);
        // Fallback sample reviews
        setReviews([
          {
            id: '1',
            user_name: 'Sarah Mitchell',
            rating: 5,
            comment: 'Joywin captured our wedding day beautifully! Every photo tells a story. Highly recommend!',
            event_type: 'civil_wedding',
            created_at: '2024-01-15'
          },
          {
            id: '2',
            user_name: 'James Thompson',
            rating: 5,
            comment: 'Amazing work on our daughter\'s baptism photos. The quality exceeded our expectations.',
            event_type: 'baptism',
            created_at: '2024-01-10'
          },
          {
            id: '3',
            user_name: 'Emily Roberts',
            rating: 5,
            comment: 'The team was so professional and friendly. Our 50th anniversary photos are treasures!',
            event_type: 'silver_golden_wedding',
            created_at: '2024-01-05'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [limit]);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#52525B]'}`}
      />
    ));
  };

  if (loading) {
    return (
      <section className="py-24 bg-[#0a0a0a]" data-testid="reviews-loading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-12 bg-[#1a1a1a] rounded w-64 mx-auto mb-12"></div>
            <div className="grid md:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-[#121212] rounded-xl p-6 h-48"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-[#0a0a0a]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[#D4AF37] tracking-[0.3em] text-sm uppercase">Testimonials</span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#EDEDED] mt-4">
            What Our <span className="gold-gradient-text">Clients Say</span>
          </h2>
        </motion.div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="reviews-grid">
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-[#121212] rounded-xl p-6 border border-white/5 relative"
              data-testid={`review-${index}`}
            >
              <Quote className="absolute top-4 right-4 w-8 h-8 text-[#D4AF37]/20" />
              
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {renderStars(review.rating)}
              </div>

              {/* Comment */}
              <p className="text-[#A1A1AA] mb-6 leading-relaxed">
                "{review.comment}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#997B1E] flex items-center justify-center">
                  <span className="text-black font-semibold text-sm">
                    {review.user_name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-[#EDEDED] font-medium">{review.user_name}</p>
                  {review.event_type && (
                    <p className="text-[#52525B] text-xs capitalize">
                      {review.event_type.replace(/_/g, ' ')}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {reviews.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#A1A1AA]">No reviews yet. Be the first to share your experience!</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;
